import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from '../../api/api.service';
import { NetworkStatusService } from './network-status.service';
import { SynchronizationService } from './synchronization.service';
import { PRODUCER_ID } from '@homeleite/common-utils';
import { ProducerSummary } from 'libs/domain/src/lib/producers';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { BehaviorSubject, forkJoin, Subject, from, Observable, of, throwError, toArray } from 'rxjs';
import { catchError, finalize, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { Controller, ControllerUser, ProducerUser } from '@homeleite/domain';
import { AnimalImageService } from '../../../../../domain/src/lib/animals/animal-image-service';

/** Progresso do sync inicial/manual — UI de login e overlay. */
export interface InitialSyncProgress {
    active: boolean;
    /** 0–100 */
    percent: number;
    /** Título curto (ex.: “Baixando animais…”). */
    label: string;
    /** Detalhe (ex.: “3 de 18”). */
    detail: string;
    done: number;
    total: number;
}

const IDLE_PROGRESS: InitialSyncProgress = {
    active: false,
    percent: 0,
    label: '',
    detail: '',
    done: 0,
    total: 0
};

/** Labels PT-BR com escapes Unicode (evita quebra de encoding no build/deploy). */
const STORE_LABELS: Record<string, string> = {
    animals: 'Animais do rebanho',
    milkControls: 'Controles leiteiros',
    milkedAnimals: 'Animais ordenhados',
    animalEvents: 'Eventos',
    properties: 'Propriedades',
    dries: 'Secagens',
    matings: 'Coberturas',
    pregnancies: 'Prenhezes',
    childbirths: 'Partos',
    herdouts: 'Sa\u00EDdas de rebanho',
    controllers: 'Controladores',
    breeds: 'Ra\u00E7as',
    companies: 'Empresas',
    deathReasons: 'Motivos de morte',
    discardReasons: 'Motivos de descarte',
    saleReasons: 'Motivos de venda',
    producers: 'Produtores',
    breeders: 'Reprodutores'
};

interface SyncResource {
    resourcePath: string;
    storeName: string;
    userFilterPropertyPath?: string;
    apiFilterParamName?: string;
    idInPath?: boolean;
}

type ApiSyncResult = {
    storeName: string;
    data: any[];
    error?: boolean;
};

export type SyncMode = 'incremental' | 'full';

type SyncOutcome = { ok: boolean; changed: boolean; maxStamp: number | null };

type SyncState = { changeAfter: string | null; fullAt: number | null };

/** Endpoints com `changeAfter` na API (delta real; incluem baixas recentes). */
const DELTA_STORES = new Set(['animals', 'animalEvents', 'pregnancies']);
/**
 * Sempre no incremental (sem buraco de 12h): milk + eventos tipados + breeders.
 */
const ALWAYS_FULL_STORES = new Set([
    'milkControls',
    'dries',
    'matings',
    'childbirths',
    'herdouts',
    'breeders'
]);
/** Eventos removidos no servidor vêm no delta com `isActive: false`: apagar do IndexedDB. */
const SOFT_DELETED_STORES = new Set(['animalEvents', 'pregnancies']);
/**
 * A tela de eventos lê stores tipados (não `animalEvents`).
 * Quando o delta traz exclusão (Admin / SRG-SCL), apaga o mesmo id nesses stores.
 */
const TYPED_EVENT_STORES = ['matings', 'dries', 'childbirths', 'herdouts', 'pregnancies'] as const;
/** Demais stores (catálogos…) só no sync completo. */
const FULL_SYNC_EVERY_MS = 12 * 60 * 60 * 1000;
/** Sobreposição do watermark: upsert é idempotente, perder alteração não. */
const WATERMARK_OVERLAP_MS = 2 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class InitialSyncService {
    private readonly iconsToSync: string[] = [
        "images/cow-icon.svg",
        "images/milk-icon.svg",
        "images/cow-management-icon.svg",
        "images/bottle-icon.svg",
        "images/cow-milking-icon.svg",
        "images/position-animal.svg",
        "images/clean-button-icon.svg",
        "images/green-bottle.svg",
        "images/cow-milk-control.svg",
        "images/clock-milk-control.svg",
        "images/filled-bottle.svg",
        "images/dump-icon.svg",
        "images/search-icon.svg",
        "images/flask-icon.svg",
        "images/cow-icon-green.svg",
        "images/cow-icon-gray.svg",
        "images/dry-confirmation.svg",
        "images/warning-icon.svg",
        "images/cow-milking-green-icon.svg",
        "images/bell-icon.svg",
        "images/edit-icon.svg",
        "images/arrow-icon.svg",
        "images/milk-confirmation-icon.svg",
        "images/report-image.png",
        "images/loading.svg"
    ];

    private readonly resourcesToSync: SyncResource[] = [
        {
            resourcePath: 'animals/producer/sync/',
            storeName: 'animals',
            userFilterPropertyPath: PRODUCER_ID,
            apiFilterParamName: 'producerId',
            idInPath: true
        },
        {
            resourcePath: 'milkControls/unfinishedBy',
            storeName: 'milkControls',
            userFilterPropertyPath: PRODUCER_ID,
            apiFilterParamName: 'producerId'
        },
        {
            resourcePath: 'milkControls/lastFinishedBy',
            storeName: 'milkControls',
            userFilterPropertyPath: PRODUCER_ID,
            apiFilterParamName: 'producerId'
        },
        {
            resourcePath: 'animalEvents/producer',
            storeName: 'animalEvents',
            userFilterPropertyPath: PRODUCER_ID,
            apiFilterParamName: 'producerId'
        },
        {
            resourcePath: 'properties',
            storeName: 'properties',
            userFilterPropertyPath: PRODUCER_ID,
            apiFilterParamName: 'producerId_EQ'
        },
        {
            resourcePath: 'dries',
            storeName: 'dries',
            userFilterPropertyPath: PRODUCER_ID,
            apiFilterParamName: 'producerId_EQ'
        },
        {
            resourcePath: 'matings',
            storeName: 'matings',
            userFilterPropertyPath: PRODUCER_ID,
            apiFilterParamName: 'producerId_EQ'
        },
        {
            resourcePath: 'pregnancies/producer',
            storeName: 'pregnancies',
            userFilterPropertyPath: PRODUCER_ID,
            apiFilterParamName: 'producerId'
        },
        {
            resourcePath: 'childBirths',
            storeName: 'childbirths',
            userFilterPropertyPath: PRODUCER_ID,
            apiFilterParamName: 'producerId_EQ'
        },
        {
            resourcePath: 'herd-outs',
            storeName: 'herdouts',
            userFilterPropertyPath: PRODUCER_ID,
            apiFilterParamName: 'producerId_EQ'
        },
        { resourcePath: 'controllers', storeName: 'controllers' },
        { resourcePath: 'breeds', storeName: 'breeds' },
        { resourcePath: 'companies', storeName: 'companies' },
        { resourcePath: 'reasons/herdOutByDeath', storeName: 'deathReasons' },
        { resourcePath: 'reasons/herdOutByDiscard', storeName: 'discardReasons' },
        { resourcePath: 'reasons/herdOutBySale', storeName: 'saleReasons' },
        { resourcePath: 'producers', storeName: 'producers' },
        { resourcePath: 'breeders', storeName: 'breeders' }
    ];

    private syncCompletedForUserId: string | null = null;
    private syncInFlight = false;
    /** When true, sync does not emit progress (no animation overlay). */
    private silentProgress = false;
    private _dataRefreshed$ = new Subject<void>();
    public dataRefreshed$ = this._dataRefreshed$.asObservable();

    private readonly _progress$ = new BehaviorSubject<InitialSyncProgress>(IDLE_PROGRESS);
    public readonly progress$ = this._progress$.asObservable();

    constructor(
        private networkStatusService: NetworkStatusService,
        private apiService: ApiService,
        private dbService: NgxIndexedDBService,
        private imageService: AnimalImageService,
        private http: HttpClient,
        private synchronizationService: SynchronizationService
    ) {
    }

    /**
     * `mode` omitido: incremental se ja houve sync completo recente com dados no
     * IndexedDB; senao completo. Login e sync manual pedem `full`.
     * `silent: true` — sync em background sem overlay (reabrir app / timer / SSE).
     */
    public performUserSync(
        user: ProducerUser | ControllerUser,
        options: { mode?: SyncMode; silent?: boolean } = {}
    ): Observable<void> {
        const userIdForFlag = user?.id;

        if (!user || !userIdForFlag) {
            return throwError(
                () =>
                    new Error(
                        'Usu\u00E1rio ou ID do Usu\u00E1rio inv\u00E1lido para sincroniza\u00E7\u00E3o.'
                    )
            );
        }
        if (this.syncInFlight) {
            return of(undefined);
        }
        if (this.syncCompletedForUserId === userIdForFlag) {
            return of(undefined);
        }
        if (!this.networkStatusService.isOnline()) {
            return of(undefined);
        }

        if (!(user instanceof ControllerUser) && !(user instanceof ProducerUser)) {
            return throwError(
                () => new Error('Tipo de usu\u00E1rio desconhecido para sincroniza\u00E7\u00E3o.')
            );
        }

        const startedAt = Date.now();
        const state = this.readSyncState(userIdForFlag);
        this.silentProgress = !!options.silent;
        this.syncInFlight = true;

        this.setProgress({
            active: true,
            percent: 1,
            label: 'Preparando sincroniza\u00E7\u00E3o...',
            detail: 'Rebanho grande pode levar alguns minutos',
            done: 0,
            total: 0
        });

        return this.resolveMode(state, options.mode).pipe(
            switchMap((mode) => {
                const changeAfter = mode === 'incremental' ? state.changeAfter : null;
                const sync$ =
                    user instanceof ControllerUser
                        ? this._syncForController(user, changeAfter)
                        : this._syncForProducer(user, changeAfter);
                return sync$.pipe(
                    tap((outcome) => {
                        this.syncCompletedForUserId = userIdForFlag;
                        if (outcome.ok) {
                            this.writeSyncState(userIdForFlag, {
                                changeAfter: this.nextWatermark(outcome.maxStamp, startedAt, changeAfter),
                                fullAt: mode === 'full' ? startedAt : state.fullAt
                            });
                        }
                    })
                );
            }),
            map(() => undefined),
            finalize(() => {
                this.syncInFlight = false;
                this.clearProgress();
            })
        );
    }

    private setProgress(partial: InitialSyncProgress): void {
        if (this.silentProgress) {
            return;
        }
        this._progress$.next(partial);
    }

    private clearProgress(): void {
        this.silentProgress = false;
        this._progress$.next(IDLE_PROGRESS);
    }

    private reportDownloadProgress(storeName: string, done: number, total: number): void {
        const label = STORE_LABELS[storeName] ?? storeName;
        // Download = ate 85%; gravar/imagens = resto
        const percent = total > 0 ? Math.min(85, Math.round((done / total) * 85)) : 5;
        this.setProgress({
            active: true,
            percent,
            label: `Baixando ${label}...`,
            detail: total > 0 ? `${done} de ${total}` : '',
            done,
            total
        });
    }

    public resetSyncFlag(): void {
        this.syncCompletedForUserId = null;
    }

    private resolveMode(state: SyncState, requested?: SyncMode): Observable<SyncMode> {
        if (
            requested === 'full' ||
            !state.changeAfter ||
            !state.fullAt ||
            Date.now() - state.fullAt > FULL_SYNC_EVERY_MS
        ) {
            return of('full');
        }
        // IndexedDB apagado/recriado com watermark sobrando → incremental traria quase nada
        return this.dbService.count('animals').pipe(
            map((n): SyncMode => (n > 0 ? 'incremental' : 'full')),
            catchError(() => of<SyncMode>('full'))
        );
    }

    /** Relógio do servidor (auditData), limitado ao do aparelho e com sobreposição. */
    private nextWatermark(maxStamp: number | null, startedAt: number, previous: string | null): string {
        if (maxStamp == null) {
            return previous ?? new Date(startedAt - WATERMARK_OVERLAP_MS).toISOString();
        }
        return new Date(Math.min(maxStamp, startedAt) - WATERMARK_OVERLAP_MS).toISOString();
    }

    private syncStateKey(userId: string): string {
        return `homeleite.sync.${userId}`;
    }

    private readSyncState(userId: string): SyncState {
        try {
            const raw = JSON.parse(localStorage.getItem(this.syncStateKey(userId)) ?? 'null');
            return {
                changeAfter: typeof raw?.changeAfter === 'string' ? raw.changeAfter : null,
                fullAt: typeof raw?.fullAt === 'number' ? raw.fullAt : null
            };
        } catch {
            return { changeAfter: null, fullAt: null };
        }
    }

    private writeSyncState(userId: string, state: SyncState): void {
        try {
            localStorage.setItem(this.syncStateKey(userId), JSON.stringify(state));
        } catch {
            // sem storage: próximo sync volta a ser completo
        }
    }

    private isIncluded(resource: SyncResource, changeAfter: string | null): boolean {
        return (
            !changeAfter ||
            DELTA_STORES.has(resource.storeName) ||
            ALWAYS_FULL_STORES.has(resource.storeName)
        );
    }

    private _syncForProducer(user: ProducerUser, changeAfter: string | null): Observable<SyncOutcome> {
        const apiRequests$ = this.resourcesToSync
            .filter((resource) => this.isIncluded(resource, changeAfter))
            .map((resource) => this.buildRequestForResource(resource, user.producer, changeAfter));

        return this.executeSyncFlow(apiRequests$, true, changeAfter);
    }

    private _syncForController(user: ControllerUser, changeAfter: string | null): Observable<SyncOutcome> {
        const controllerId = user.controller?.id;
        if (!controllerId) {
            return throwError(() => new Error('Controlador sem ID na sess\u00E3o.'));
        }

        return this.apiService.root
            .path('controllers')
            .path(controllerId)
            .getJson<Controller>()
            .pipe(
                map((response) => Controller.fromJson(response)),
                // GET /controllers/:id pode 500 com DBRef órfã — tenta lista scoped de produtores.
                catchError((err) => {
                    console.error('Falha ao baixar controlador; fallback GET /producers', err);
                    return this.apiService.root
                        .path('producers')
                        .getJson<any[]>()
                        .pipe(
                            map((list) => {
                                const summaries = (Array.isArray(list) ? list : [])
                                    .map((item) => ProducerSummary.fromJson(item))
                                    .filter((p) => !!p?.id);
                                const fallback = new Controller(
                                    controllerId,
                                    user.controller?.name,
                                    '',
                                    user.controller?.code || '',
                                    user.controller?.email || ''
                                );
                                fallback.producers = summaries;
                                fallback.activeProducers = summaries;
                                return fallback;
                            }),
                            catchError((fallbackErr) => {
                                console.error('Fallback GET /producers também falhou', fallbackErr);
                                return throwError(
                                    () =>
                                        new Error(
                                            'N\u00E3o foi poss\u00EDvel baixar os produtores do controlador.'
                                        )
                                );
                            })
                        );
                }),
                switchMap((controllerToSave) => {

                    // App usa activeProducers; em HML alguns controladores só têm `producers`.
                    const producersForSync =
                        controllerToSave.activeProducers?.length > 0
                            ? controllerToSave.activeProducers
                            : controllerToSave.producers || [];

                    (controllerToSave as any).producerIds = producersForSync.map(
                        (producer) => producer.id
                    );

                    const allRequests$: Observable<ApiSyncResult>[] = [];

                    // Sempre persiste o controller da API (evita store sem produtores após update).
                    allRequests$.push(
                        of({
                            storeName: 'controllers',
                            data: [controllerToSave]
                        })
                    );

                    // Produtores associados precisam estar no store `producers`.
                    // A lista/Gestão e as telas Animais/Controle usam findSummary(id).
                    const producerSeed = new Map<string, ProducerSummary>();
                    [...(controllerToSave.activeProducers || []), ...(controllerToSave.producers || [])]
                        .filter((p) => !!p?.id)
                        .forEach((p) => producerSeed.set(p.id, p));
                    if (producerSeed.size > 0) {
                        allRequests$.push(
                            of({
                                storeName: 'producers',
                                data: Array.from(producerSeed.values()).map((p) => ({
                                    id: p.id,
                                    name: p.name,
                                    code: p.code
                                }))
                            })
                        );
                    }

                    if (producersForSync.length > 0) {
                        producersForSync.forEach((producer) => {
                            this.resourcesToSync.forEach((resource) => {
                                if (
                                    resource.userFilterPropertyPath &&
                                    this.isIncluded(resource, changeAfter)
                                ) {
                                    allRequests$.push(
                                        this.buildRequestForResource(resource, producer, changeAfter)
                                    );
                                }
                            });
                        });
                    }

                    this.resourcesToSync.forEach((resource) => {
                        if (
                            !resource.userFilterPropertyPath &&
                            resource.storeName !== 'controllers' &&
                            // Já semeado acima — evita clear+lista vazia do GET /producers.
                            resource.storeName !== 'producers' &&
                            this.isIncluded(resource, changeAfter)
                        ) {
                            allRequests$.push(
                                this.buildRequestForResource(resource, null, changeAfter)
                            );
                        }
                    });

                    return this.executeSyncFlow(allRequests$, false, changeAfter);
                })
            );
    }

    private buildRequestForResource(
        resource: SyncResource,
        producer: ProducerSummary | null,
        changeAfter: string | null = null
    ): Observable<ApiSyncResult> {
        let path = this.apiService.root.path(resource.resourcePath);
        let params = new HttpParams();
        if (changeAfter && DELTA_STORES.has(resource.storeName)) {
            params = params.set('changeAfter', changeAfter);
        }

        if (resource.userFilterPropertyPath && producer) {
            if (resource.idInPath) {
                path = path.path(producer.id);
            } else {
                params = params.set(resource.apiFilterParamName, producer.id);
            }
        } else if (resource.userFilterPropertyPath && !producer) {
            return of({ storeName: resource.storeName, data: [], error: true });
        }

        return path.getJson<any[] | any>({ params }).pipe(
            map((data) => ({
                storeName: resource.storeName,
                data: Array.isArray(data) ? data : data ? [data] : []
            })),
            catchError((error) => {
                console.error(`Erro ao buscar recurso: ${resource.storeName}`, error);
                return of({ storeName: resource.storeName, data: [], error: true });
            })
        );
    }

    /**
     * Completo (`changeAfter` nulo): substitui cada store buscado com sucesso, inclusive
     * por lista vazia. Incremental: upsert nos stores delta. Store com qualquer erro
     * fica intacto (não troca dado bom por parcial).
     */
    private executeSyncFlow(
        apiRequests$: Observable<ApiSyncResult>[],
        shouldSyncImages: boolean,
        changeAfter: string | null = null
    ): Observable<SyncOutcome> {
        // Recursos do sync são independentes entre stores; 4 reduz tempo em rebanhos grandes.
        const CONCURRENCY_LIMIT = 4;
        const incremental = !!changeAfter;
        let outcome: SyncOutcome = { ok: true, changed: false, maxStamp: null };
        const totalRequests = apiRequests$.length;
        let doneRequests = 0;
        this.setProgress({
            active: true,
            percent: 2,
            label: 'Baixando dados do servidor...',
            detail: totalRequests > 0 ? `0 de ${totalRequests}` : '',
            done: 0,
            total: totalRequests
        });

        return from(apiRequests$).pipe(
            mergeMap(
                (request$) =>
                    request$.pipe(
                        tap((result) => {
                            doneRequests += 1;
                            this.reportDownloadProgress(result.storeName, doneRequests, totalRequests);
                        })
                    ),
                CONCURRENCY_LIMIT
            ),
            toArray(),

            switchMap((results: ApiSyncResult[]) =>
                this.synchronizationService.hasDirtyStores(['milkControls', 'milkedAnimals']).pipe(
                    switchMap((milkQueueDirty) => {
                this.setProgress({
                    active: true,
                    percent: 88,
                    label: 'Gravando no aparelho...',
                    detail: 'Montando o banco local',
                    done: doneRequests,
                    total: totalRequests
                });

                const dataByStore = new Map<string, any[]>();
                const failedStores = new Set(results.filter((r) => r.error).map((r) => r.storeName));

                results.forEach((result) => {
                    if (failedStores.has(result.storeName)) {
                        return;
                    }
                    const existingData = dataByStore.get(result.storeName) || [];
                    dataByStore.set(result.storeName, [
                        ...existingData,
                        ...(Array.isArray(result.data) ? result.data : [])
                    ]);
                });

                // Zero perda: com milk ainda na fila local, não aplicar pull (evita wipe).
                if (milkQueueDirty) {
                    dataByStore.delete('milkControls');
                    dataByStore.delete('milkedAnimals');
                    console.warn(
                        '[SYNC] Pull de milkControls/milkedAnimals adiado — syncQueue local dirty'
                    );
                }

                const milkControls = dataByStore.get('milkControls');

                if (milkControls) {
                    dataByStore.set('milkedAnimals', dataByStore.get('milkedAnimals') ?? []);
                }

                if (milkControls && milkControls.length > 0) {
                    const extractedMilkedAnimals: any[] = [];

                    milkControls.forEach((control: any) => {
                        if (control.milkings && Array.isArray(control.milkings)) {
                            control.milkings.forEach((milking: any) => {
                                if (milking.milkedAnimals && Array.isArray(milking.milkedAnimals)) {
                                    milking.milkedAnimals.forEach((animal: any) => {
                                        animal.milkControlId = control.id;
                                        animal.milkingId = milking.id;
                                        extractedMilkedAnimals.push(animal);
                                    });
                                    // Não guardar o blob no controle — já vai para o store milkedAnimals.
                                    // Sem isso o IDB fica com MBs duplicados e a home trava no getAll.
                                    milking.milkedAnimals = [];
                                }
                            });
                        }
                    });

                    if (extractedMilkedAnimals.length > 0) {
                        const existingAnimals = dataByStore.get('milkedAnimals') || [];
                        dataByStore.set('milkedAnimals', [...existingAnimals, ...extractedMilkedAnimals]);
                    }
                }

                let deltaChanged = false;
                let maxStamp: number | null = null;
                DELTA_STORES.forEach((storeName) => {
                    const data = dataByStore.get(storeName) ?? [];
                    deltaChanged ||= data.length > 0;
                    data.forEach((item) => {
                        const stamp = this.latestAuditStamp(item);
                        if (stamp != null && (maxStamp == null || stamp > maxStamp)) {
                            maxStamp = stamp;
                        }
                    });
                });
                outcome = {
                    ok: failedStores.size === 0,
                    changed: !incremental || deltaChanged,
                    maxStamp
                };

                const removedByStore = new Map<string, IDBValidKey[]>();
                SOFT_DELETED_STORES.forEach((storeName) => {
                    const data = dataByStore.get(storeName);
                    const removed = data?.filter((item) => this.isSoftDeleted(item)) ?? [];
                    if (data && removed.length > 0) {
                        removedByStore.set(storeName, removed.map((item) => item.id));
                        dataByStore.set(storeName, data.filter((item) => !this.isSoftDeleted(item)));
                    }
                });

                const dbOperations$: Observable<any>[] = Array.from(dataByStore.entries())
                    .filter(([storeName, data]) => !(incremental && DELTA_STORES.has(storeName) && data.length === 0))
                    .map(([storeName, data]) => {
                        const normalized =
                            storeName === 'animals'
                                ? data.map((item) => this.normalizeAnimalForStore(item))
                                : data;
                        if (incremental && DELTA_STORES.has(storeName)) {
                            return this.dbService.bulkPut(storeName, normalized);
                        }
                        // milkControls só chega aqui se a fila local não estiver dirty (acima).
                        return this.dbService
                            .clear(storeName)
                            .pipe(
                                switchMap(() =>
                                    normalized.length > 0
                                        ? this.dbService.bulkAdd(storeName, normalized)
                                        : of(null)
                                )
                            );
                    });

                if (incremental) {
                    removedByStore.forEach((ids, storeName) =>
                        dbOperations$.push(this.dbService.bulkDelete(storeName, ids))
                    );
                    // Exclusão via Admin/SRG-SCL: limpa também os stores tipados da UI.
                    const eventIds = removedByStore.get('animalEvents') ?? [];
                    if (eventIds.length > 0) {
                        TYPED_EVENT_STORES.forEach((storeName) => {
                            if (!removedByStore.has(storeName)) {
                                dbOperations$.push(this.dbService.bulkDelete(storeName, eventIds));
                            }
                        });
                    }
                } else {
                    dbOperations$.push(this.syncAppIcons());
                }

                const initialDataSync$ =
                    dbOperations$.length > 0 ? forkJoin(dbOperations$) : of(null);

                return initialDataSync$.pipe(
                    switchMap(() => this.applyHerdOutsFromEvents(dataByStore.get('animalEvents') ?? [])),
                    tap(() => {
                        if (outcome.changed) this._dataRefreshed$.next();
                    }),
                    switchMap(() => {
                        if (!shouldSyncImages) {
                            this.setProgress({
                                active: true,
                                percent: 100,
                                label: 'Quase pronto...',
                                detail: '',
                                done: doneRequests,
                                total: totalRequests
                            });
                            return of(undefined);
                        }

                        const animalsData = dataByStore.get('animals');
                        if (!animalsData) {
                            return of(undefined);
                        }

                        const integrationAnimals$ = animalsData
                            .filter(animal =>
                                animal.auditData?.createdByIntegration &&
                                animal.imageUrl?.startsWith('data:image')
                            )
                            .map(animal =>
                                this.imageService.saveBase64Image(animal.id, animal.imageUrl)
                            );

                        const allImageTasks$ = [...integrationAnimals$];

                        if (allImageTasks$.length === 0) {
                            this.setProgress({
                                active: true,
                                percent: 100,
                                label: 'Sincroniza\u00E7\u00E3o conclu\u00EDda',
                                detail: '',
                                done: doneRequests,
                                total: totalRequests
                            });
                            return of(undefined);
                        }

                        this.setProgress({
                            active: true,
                            percent: 92,
                            label: 'Preparando fotos...',
                            detail: `${allImageTasks$.length} imagem(ns)`,
                            done: doneRequests,
                            total: totalRequests
                        });

                        const IMAGE_CONCURRENCY = 4;

                        return from(allImageTasks$).pipe(
                            mergeMap(task => task, IMAGE_CONCURRENCY),
                            toArray(),
                            map(() => undefined)
                        );
                    })
                );
                    })
                )
            ),
            map(() => outcome),
            catchError((error) => {
                console.error(
                    'Falha cr\u00EDtica durante a sincroniza\u00E7\u00E3o com o banco de dados.',
                    error
                );
                return of<SyncOutcome>({ ok: false, changed: outcome.changed, maxStamp: null });
            })
        );
    }

    private isSoftDeleted(item: any): boolean {
        return item?.isActive === false || item?.active === false;
    }

    private latestAuditStamp(item: any): number | null {
        const a = item?.auditData;
        if (!a) {
            return null;
        }
        let max: number | null = null;
        for (const v of [a.created, a.modifiedByApp, a.modifiedByIntegration, a.createdByApp, a.createdByIntegration]) {
            const t = v ? new Date(v).getTime() : NaN;
            if (!Number.isNaN(t) && (max == null || t > max)) {
                max = t;
            }
        }
        return max;
    }

    private normalizeAnimalForStore(animal: any): any {
        if (!animal) {
            return animal;
        }
        const situation = animal.situation ?? null;
        const leftHerd = situation === 'B' || situation === 'b';
        return {
            ...animal,
            situation,
            isActive: leftHerd ? false : animal.isActive ?? animal.active ?? true
        };
    }

    /** Eventos de baixa sincronizados → marca o animal como fora do rebanho no IndexedDB. */
    private applyHerdOutsFromEvents(events: any[]): Observable<unknown> {
        const herdTypes = new Set(['herdOutByDeath', 'herdOutByDiscard', 'herdOutBySale']);
        const animalIds = [
            ...new Set(
                (events ?? [])
                    .filter((e) => herdTypes.has(e?.type))
                    .map((e) => e?.animal?.id ?? e?.animal?.$id ?? e?.animal)
                    .filter((id): id is string => typeof id === 'string' && !!id)
            )
        ];
        if (animalIds.length === 0) {
            return of(null);
        }
        return from(animalIds).pipe(
            mergeMap((id) =>
                this.dbService.getByKey<any>('animals', id).pipe(
                    switchMap((animal) =>
                        animal
                            ? this.dbService.update('animals', { ...animal, isActive: false, situation: 'B' })
                            : of(null)
                    ),
                    catchError(() => of(null))
                ),
                4
            ),
            toArray()
        );
    }

    private syncAppIcons(): Observable<any> {
        const iconRequests$ = this.iconsToSync.map(iconName => {
            const path = `assets/${iconName}`;
            return this.http.get(path, { responseType: 'blob' }).pipe(
                map(blobData => ({
                    name: iconName,
                    image: blobData
                })),
                catchError(err => {
                    console.warn(`Erro ao baixar ícone ${iconName}`, err);
                    return of(null);
                })
            );
        });

        return forkJoin(iconRequests$).pipe(
            switchMap(icons => {
                const validIcons = icons.filter(i => i !== null);
                if (validIcons.length === 0) return of(true);

                return this.dbService.clear('icons').pipe(
                    switchMap(() => this.dbService.bulkAdd('icons', validIcons))
                );
            })
        );
    }
}
